import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { Card } from '@/components/Card'
import { useAuth } from '@/hooks/useAuth'
import { useGeolocation, useGeolocationData } from '@/hooks/useGeolocation'
import { MapPin, Navigation, Save, RefreshCw } from 'lucide-react'

const geolocationSchema = z.object({
  entity_type: z.enum(['user', 'servidor', 'discente']),
  entity_id: z.number().min(1, 'ID da entidade é obrigatório'),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  nome_unidade: z.string().min(1, 'Nome da unidade é obrigatório'),
  sigla_unidade: z.string().min(1, 'Sigla da unidade é obrigatória'),
  accuracy: z.number().int('Precisão deve ser um número inteiro').optional(),
  source: z.string().optional(),
})

type GeolocationFormData = z.infer<typeof geolocationSchema>

export function GeolocationPage() {
  const { user } = useAuth()
  const { 
    position, 
    loading: geoLoading, 
    getCurrentPosition, 
    getCurrentPositionHighAccuracy, 
    getCurrentPositionFast,
    accuracy,
    error: geoError
  } = useGeolocation()
  const { createGeolocation, loading: submitLoading } = useGeolocationData()
  const [showMap, setShowMap] = useState(false)
  const [geoMode, setGeoMode] = useState<'normal' | 'high' | 'fast'>('normal')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  // Helper function to check if coordinates are valid
  const hasValidCoordinates = (lat: any, lng: any) => {
    const numLat = Number(lat)
    const numLng = Number(lng)
    return lat !== undefined && lng !== undefined && 
           !isNaN(numLat) && !isNaN(numLng) && 
           numLat !== 0 && numLng !== 0 &&
           numLat >= -90 && numLat <= 90 &&
           numLng >= -180 && numLng <= 180
  }

  // Helper function to safely format coordinates
  const formatCoordinate = (coord: any) => {
    const num = Number(coord)
    return !isNaN(num) ? num.toFixed(6) : '0.000000'
  }

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<GeolocationFormData>({
    resolver: zodResolver(geolocationSchema),
    defaultValues: {
      entity_type: 'user',
      entity_id: 1,
      latitude: undefined,
      longitude: undefined,
      nome_unidade: '',
      sigla_unidade: '',
      accuracy: undefined,
      source: 'manual',
    },
  })

  const watchedEntityType = watch('entity_type')
  const watchedLatitude = watch('latitude')
  const watchedLongitude = watch('longitude')

  // Atualizar entity_id quando user estiver disponível
  useEffect(() => {
    if (user?.id) {
      setValue('entity_id', user.id)
      
      // Definir tipo baseado no usuário
      if (user.servidor) {
        setValue('entity_type', 'servidor')
      } else if (user.discente) {
        setValue('entity_type', 'discente')
      } else {
        setValue('entity_type', 'user')
      }
    }
  }, [user, setValue])

  // Atualizar coordenadas quando posição GPS estiver disponível
  useEffect(() => {
    if (position?.coords) {
      setValue('latitude', Number(position.coords.latitude.toFixed(6)))
      setValue('longitude', Number(position.coords.longitude.toFixed(6)))
      setValue('accuracy', Math.round(position.coords.accuracy))
      setValue('source', 'gps')
    }
  }, [position, setValue])

  const handleGetCurrentLocation = async (mode: 'normal' | 'high' | 'fast' = 'normal') => {
    try {
      setGeoMode(mode)
      let position
      
      switch (mode) {
        case 'high':
          position = await getCurrentPositionHighAccuracy()
          toast.success('Localização de alta precisão obtida!')
          break
        case 'fast':
          position = await getCurrentPositionFast()
          toast.success('Localização rápida obtida!')
          break
        default:
          position = await getCurrentPosition()
          toast.success('Localização obtida com sucesso!')
      }
      
      return position
    } catch (error: any) {
      toast.error(error.message || 'Erro ao obter localização')
    }
  }

  const onSubmit = async (data: GeolocationFormData) => {
    try {
      await createGeolocation(data, selectedFile || undefined)
      toast.success('Geolocalização salva com sucesso!')
      
      // Limpar formulário após sucesso
      reset()
      setSelectedFile(null)
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao salvar geolocalização'
      toast.error(message)
    }
  }

  return (
    <div className="w-full">
      {/* Conteúdo principal */}
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">
            Cadastro de Geolocalização
          </h1>
          <p className="text-slate-600 mt-2">
            Insira os dados de localização para o sistema
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulário */}
          <Card title="Dados de Localização">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Tipo de Entidade
                  </label>
                  <select
                    {...register('entity_type')}
                    className="block w-full px-3 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="user">Usuário</option>
                    <option value="servidor">Servidor</option>
                    <option value="discente">Discente</option>
                  </select>
                  {errors.entity_type && (
                    <p className="text-sm text-red-600 mt-1">{errors.entity_type.message}</p>
                  )}
                </div>

                <div>
                  <Input
                    {...register('entity_id', { valueAsNumber: true })}
                    type="number"
                    label="ID da Entidade"
                    error={errors.entity_id?.message}
                    placeholder="ID do usuário/servidor/discente"
                    
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-slate-900">Coordenadas</h3>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGetCurrentLocation('normal')}
                      loading={geoLoading && geoMode === 'normal'}
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Obter Localização
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGetCurrentLocation('high')}
                      loading={geoLoading && geoMode === 'high'}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Alta Precisão
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGetCurrentLocation('fast')}
                      loading={geoLoading && geoMode === 'fast'}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Rápido
                    </Button>
                  </div>
                  
                  {accuracy && (
                    <div className="text-xs text-slate-500">
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100">
                        📍 Precisão: {accuracy.toFixed(0)}m
                      </span>
                    </div>
                  )}
                  
                  {geoError && (
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                      {geoError}
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
                  <p><strong>Modos de localização:</strong></p>
                  <ul className="mt-1 space-y-1">
                    <li>• <strong>Normal:</strong> Balanceado entre velocidade e precisão</li>
                    <li>• <strong>Alta Precisão:</strong> Usa GPS para maior exatidão (pode demorar mais)</li>
                    <li>• <strong>Rápido:</strong> Localização aproximada usando rede WiFi/celular</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    {...register('latitude', { valueAsNumber: true })}
                    type="number"
                    step="any"
                    label="Latitude"
                    error={errors.latitude?.message}
                    placeholder="-23.550520"
                  />

                  <Input
                    {...register('longitude', { valueAsNumber: true })}
                    type="number"
                    step="any"
                    label="Longitude"
                    error={errors.longitude?.message}
                    placeholder="-46.633308"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-slate-900">Informações da Unidade</h3>
                
                <Input
                  {...register('nome_unidade')}
                  label="Nome da Unidade"
                  error={errors.nome_unidade?.message}
                  placeholder="Campus Macapá"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    {...register('sigla_unidade')}
                    label="Sigla da Unidade"
                    error={errors.sigla_unidade?.message}
                    placeholder="IFAP-MCP"
                  />

                  <Input
                    {...register('accuracy', { valueAsNumber: true })}
                    label="Precisão (metros)"
                    type="number"
                    min="1"
                    step="1"
                    error={errors.accuracy?.message}
                    placeholder="10"
                  />
                </div>

                {/* Campo de upload de arquivo */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Arquivo da Unidade (Opcional)
                  </label>
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
                  />
                  {selectedFile && (
                    <p className="text-sm text-slate-600 mt-1">
                      Arquivo selecionado: {selectedFile.name}
                    </p>
                  )}
                </div>

                {/* Campo oculto para source */}
                <input
                  type="hidden"
                  {...register('source')}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                loading={submitLoading}
                size="lg"
              >
                <Save className="w-4 h-4 mr-2" />
                {submitLoading ? 'Salvando...' : 'Salvar Geolocalização'}
              </Button>
            </form>
          </Card>

          {/* Informações e Preview */}
          <div className="space-y-6">
            <Card title="Informações Atuais">
              <div className="space-y-4">
                {position && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-2">
                      Localização Detectada
                    </h4>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>Latitude: {position.coords.latitude.toFixed(6)}</p>
                      <p>Longitude: {position.coords.longitude.toFixed(6)}</p>
                      <p>
                        Precisão: {position.coords.accuracy.toFixed(0)}m 
                        <span className={`ml-2 px-2 py-1 rounded text-xs ${
                          position.coords.accuracy <= 10 
                            ? 'bg-green-200 text-green-800' 
                            : position.coords.accuracy <= 50 
                            ? 'bg-yellow-200 text-yellow-800' 
                            : 'bg-red-200 text-red-800'
                        }`}>
                          {position.coords.accuracy <= 10 
                            ? 'Excelente' 
                            : position.coords.accuracy <= 50 
                            ? 'Boa' 
                            : 'Baixa'}
                        </span>
                      </p>
                      {position.coords.altitude && (
                        <p>Altitude: {position.coords.altitude.toFixed(0)}m</p>
                      )}
                      <p className="text-xs text-green-600 mt-2 flex items-center">
                        🛰️ Capturada automaticamente via GPS
                        {position.timestamp && (
                          <span className="ml-2">
                            • {new Date(position.timestamp).toLocaleTimeString()}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {user && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">
                      Usuário Logado
                    </h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p><strong>Nome:</strong> {user.nome}</p>
                      <p><strong>Login:</strong> {user.login}</p>
                      <p><strong>ID:</strong> {user.id}</p>
                      {user.servidor && (
                        <p><strong>Tipo:</strong> Servidor</p>
                      )}
                      {user.discente && (
                        <p><strong>Tipo:</strong> Discente</p>
                      )}
                      {!user.servidor && !user.discente && (
                        <p><strong>Tipo:</strong> Usuário Padrão</p>
                      )}
                    </div>
                  </div>
                )}

                {hasValidCoordinates(watchedLatitude, watchedLongitude) && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">
                      Coordenadas do Formulário
                    </h4>
                    <div className="text-sm text-slate-700 space-y-1">
                      <p><strong>Latitude:</strong> {formatCoordinate(watchedLatitude)}</p>
                      <p><strong>Longitude:</strong> {formatCoordinate(watchedLongitude)}</p>
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setShowMap(!showMap)}
                          className="text-primary-600 hover:text-primary-700 underline text-sm"
                        >
                          {showMap ? '🗺️ Ocultar Mapa' : '📍 Ver no Mapa'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {(!position || !position.coords) && !hasValidCoordinates(watchedLatitude, watchedLongitude) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-900 mb-2">
                      Nenhuma Localização Disponível
                    </h4>
                    <div className="text-sm text-yellow-700">
                      <p>• Clique em "Obter Localização Atual" para capturar via GPS</p>
                      <p>• Ou preencha as coordenadas manualmente</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {showMap && hasValidCoordinates(watchedLatitude, watchedLongitude) && (
              <Card title="Visualização no Mapa">
                <div className="h-64 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-slate-300">
                  <MapPin className="w-8 h-8 text-slate-400 mb-2" />
                  <div className="text-center">
                    <p className="text-slate-600 font-medium mb-1">
                      Mapa Interativo
                    </p>
                    <p className="text-sm text-slate-500 mb-3">
                      Coordenadas: {Number(watchedLatitude).toFixed(6)}, {Number(watchedLongitude).toFixed(6)}
                    </p>
                    <div className="text-xs text-slate-400 space-y-1">
                      <p>🗺️ Google Maps ou OpenStreetMap seria exibido aqui</p>
                      <p>📍 Marcador na localização especificada</p>
                      <p>🔍 Controles de zoom e navegação</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      const url = `https://www.google.com/maps?q=${watchedLatitude},${watchedLongitude}`
                      window.open(url, '_blank')
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 underline"
                  >
                    🌐 Abrir no Google Maps
                  </button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
